import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Template, TemplateVariable } from '../types';
import { 
  getTemplateById, 
  updateTemplate, 
  generateTemplatePreview 
} from '../services/api';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '../constants';

const TemplateDetail: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();

  const [template, setTemplate] = useState<Template | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    textPreview: string;
    htmlPreview: string;
  } | null>(null);

  // 编辑状态
  const [editedTemplate, setEditedTemplate] = useState<Partial<Template>>({});

  useEffect(() => {
    if (templateId) {
      fetchTemplateDetails();
      loadTemplatePreview();
    }
  }, [templateId]);

  const fetchTemplateDetails = async () => {
    try {
      setLoading(true);
      const response = await getTemplateById(templateId!);
      
      if (response.success) {
        setTemplate(response.template);
        setEditedTemplate({
          name: response.template.name,
          content: response.template.content
        });
      } else {
        throw new Error(response.error || ERROR_MESSAGES.FETCH_TEMPLATE_FAILED);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.FETCH_TEMPLATE_FAILED;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplatePreview = async () => {
    try {
      const previewResponse = await generateTemplatePreview(templateId!);
      
      if (previewResponse.success && previewResponse.preview) {
        setPreview({
          textPreview: previewResponse.preview.textPreview,
          htmlPreview: previewResponse.preview.htmlPreview
        });
      }
    } catch (error) {
      console.error('预览加载失败:', error);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      if (!templateId) return;

      const response = await updateTemplate(templateId, editedTemplate);
      
      if (response.success) {
        setTemplate(response.template);
        setEditMode(false);
        alert(SUCCESS_MESSAGES.TEMPLATE_UPDATED);
      } else {
        throw new Error(response.error || ERROR_MESSAGES.UPDATE_TEMPLATE_FAILED);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.UPDATE_TEMPLATE_FAILED;
      alert(`更新失败: ${errorMessage}`);
    }
  };

  const handleVariableEdit = (index: number, updates: Partial<TemplateVariable>) => {
    if (!template) return;

    const updatedVariables = [...template.variables];
    updatedVariables[index] = { ...updatedVariables[index], ...updates };

    setEditedTemplate(prev => ({
      ...prev,
      variables: updatedVariables
    }));
  };

  const renderVariableEditor = () => {
    if (!template) return null;

    return (
      <div className="variables-section">
        <h3>模板变量</h3>
        {template.variables.map((variable, index) => (
          <div key={index} className="variable-item">
            <div className="variable-name">{variable.name}</div>
            <select 
              value={variable.type}
              onChange={(e) => handleVariableEdit(index, { 
                type: e.target.value as TemplateVariable['type'] 
              })}
              className="form-select"
            >
              <option value="text">文本</option>
              <option value="number">数字</option>
              <option value="date">日期</option>
              <option value="select">选择</option>
            </select>
            <input 
              type="text"
              value={variable.label}
              onChange={(e) => handleVariableEdit(index, { label: e.target.value })}
              placeholder="显示标签"
              className="form-input"
            />
            <label className="checkbox-label">
              <input 
                type="checkbox"
                checked={variable.required}
                onChange={(e) => handleVariableEdit(index, { required: e.target.checked })}
              />
              必填
            </label>
          </div>
        ))}
      </div>
    );
  };

  const renderTemplatePreview = () => {
    if (!preview) return <div>加载预览中...</div>;

    return (
      <div className="template-preview">
        <h3>模板预览</h3>
        <div 
          className="preview-html" 
          dangerouslySetInnerHTML={{ __html: preview.htmlPreview }}
        />
        <div className="preview-text">
          <h4>文本预览</h4>
          <p>{preview.textPreview}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="template-detail loading">
        <div className="loading-spinner"></div>
        <p>加载模板详情...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="template-detail error">
        <p>加载模板失败</p>
        <p>{error}</p>
        <button onClick={fetchTemplateDetails} className="btn btn-secondary">
          重新加载
        </button>
      </div>
    );
  }

  if (!template) return null;

  return (
    <div className="template-detail">
      <div className="template-header">
        <h2>{editMode ? '编辑模板' : template.name || '模板详情'}</h2>
        <div className="template-actions">
          {!editMode ? (
            <button 
              onClick={() => setEditMode(true)} 
              className="btn btn-secondary"
            >
              编辑模板
            </button>
          ) : (
            <>
              <button 
                onClick={handleSaveTemplate} 
                className="btn btn-primary"
              >
                保存更改
              </button>
              <button 
                onClick={() => setEditMode(false)} 
                className="btn btn-secondary"
              >
                取消
              </button>
            </>
          )}
        </div>
      </div>

      <div className="template-content">
        {editMode ? (
          <div className="edit-section">
            <div className="form-group">
              <label>模板名称</label>
              <input 
                type="text"
                value={editedTemplate.name || ''}
                onChange={(e) => setEditedTemplate(prev => ({
                  ...prev,
                  name: e.target.value
                }))}
                className="form-input"
                placeholder="输入模板名称"
              />
            </div>

            <div className="form-group">
              <label>模板内容（HTML）</label>
              <textarea 
                value={editedTemplate.content || ''}
                onChange={(e) => setEditedTemplate(prev => ({
                  ...prev,
                  content: e.target.value
                }))}
                className="form-textarea"
                rows={10}
                placeholder="输入HTML模板内容"
              />
            </div>

            {renderVariableEditor()}
          </div>
        ) : (
          <>
            {renderTemplatePreview()}
            <div className="template-metadata">
              <h3>模板信息</h3>
              <p><strong>创建时间：</strong>{new Date(template.createdAt).toLocaleString()}</p>
              <p><strong>最后更新：</strong>{new Date(template.updatedAt).toLocaleString()}</p>
              <p><strong>变量数量：</strong>{template.variables.length}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TemplateDetail;

